import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { getResume, updateResume } from '../../../services/api'
import { supabase } from '../../../lib/supabase'
import { extractTextFromPdf, parseResumeText } from '../../../utils/resumeParser'
import type { Resume, ResumeExperience, ResumeEducation } from '../../../types/database'

const springConfig = { type: "spring", stiffness: 60, damping: 10 }

export default function DashboardResume() {
  const [resume, setResume] = useState<Resume | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [extracting, setExtracting] = useState(false)
  const [extractionPreview, setExtractionPreview] = useState<Resume | null>(null)
  const [showPreview, setShowPreview] = useState(false)
  const [skillInput, setSkillInput] = useState('')

  useEffect(() => {
    loadResume()
  }, [])

  const loadResume = async () => {
    setLoading(true)
    const data = await getResume()
    if (!data) {
      setResume({
        id: '1',
        name: '',
        title: '',
        email: '',
        phone: '',
        location: '',
        summary: '',
        experience: [],
        education: [],
        skills: [],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
    } else {
      setResume(data)
    }
    setLoading(false)
  }

  const handleSave = async () => {
    if (!resume) return
    setSaving(true)
    try {
      await updateResume(resume)
      alert('Resume updated successfully!')
    } catch (error) {
      console.error('Error updating resume:', error)
      alert('Error updating resume')
    } finally {
      setSaving(false)
    }
  }

  const updateField = (field: keyof Resume, value: any) => {
    if (resume) {
      setResume({ ...resume, [field]: value })
    }
  }

  const addExperience = () => {
    if (resume) {
      const newExp: ResumeExperience = {
        id: crypto.randomUUID(),
        title: '',
        company: '',
        years: '',
        description: '',
        skills: [],
        order: resume.experience.length,
      }
      setResume({ ...resume, experience: [...resume.experience, newExp] })
    }
  }

  const updateExperience = (index: number, field: keyof ResumeExperience, value: any) => {
    if (resume) {
      const experience = [...resume.experience]
      experience[index] = { ...experience[index], [field]: value }
      setResume({ ...resume, experience })
    }
  }

  const removeExperience = (index: number) => {
    if (resume) {
      const experience = resume.experience.filter((_, i) => i !== index)
      setResume({ ...resume, experience })
    }
  }

  const addEducation = () => {
    if (resume) {
      const newEdu: ResumeEducation = {
        id: crypto.randomUUID(),
        degree: '',
        university: '',
        years: '',
        details: '',
        order: resume.education.length,
      }
      setResume({ ...resume, education: [...resume.education, newEdu] })
    }
  }

  const updateEducation = (index: number, field: keyof ResumeEducation, value: any) => {
    if (resume) {
      const education = [...resume.education]
      education[index] = { ...education[index], [field]: value }
      setResume({ ...resume, education })
    }
  }

  const removeEducation = (index: number) => {
    if (resume) {
      const education = resume.education.filter((_, i) => i !== index)
      setResume({ ...resume, education })
    }
  }

  const addSkill = () => {
    if (skillInput.trim() && resume) {
      const skills = [...(resume.skills || []), skillInput.trim()]
      setResume({ ...resume, skills })
      setSkillInput('')
    }
  }

  const removeSkill = (index: number) => {
    if (resume) {
      const skills = resume.skills.filter((_, i) => i !== index)
      setResume({ ...resume, skills })
    }
  }

  const handlePdfUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !resume) return

    // Validate file type
    if (file.type !== 'application/pdf') {
      alert('Please upload a PDF file')
      return
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB')
      return
    }

    setUploading(true)
    try {
      // Delete old PDF if exists
      if (resume.pdf_url) {
        const oldPath = resume.pdf_url.split('/').pop()
        if (oldPath) {
          await supabase.storage.from('resumes').remove([oldPath])
        }
      }

      // Upload new PDF
      const fileExt = file.name.split('.').pop()
      const fileName = `resume-${Date.now()}.${fileExt}`
      const filePath = fileName

      const { error: uploadError } = await supabase.storage
        .from('resumes')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        })

      if (uploadError) {
        throw uploadError
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('resumes')
        .getPublicUrl(filePath)

      if (urlData?.publicUrl) {
        setResume({ ...resume, pdf_url: urlData.publicUrl })
        // Auto-save the PDF URL
        await updateResume({ ...resume, pdf_url: urlData.publicUrl })
        alert('Resume PDF uploaded successfully!')
      }
    } catch (error: any) {
      console.error('Error uploading PDF:', error)
      const errorMessage = error?.message || 'Unknown error occurred'
      if (errorMessage.includes('Bucket not found') || errorMessage.includes('does not exist')) {
        alert('Storage bucket "resumes" not found. Please create it in Supabase Storage settings.')
      } else if (errorMessage.includes('new row violates row-level security')) {
        alert('Permission denied. Please check your Supabase Storage policies.')
      } else {
        alert(`Error uploading PDF: ${errorMessage}. Please check the console for details.`)
      }
    } finally {
      setUploading(false)
      // Reset input
      event.target.value = ''
    }
  }

  const handleDeletePdf = async () => {
    if (!resume?.pdf_url || !confirm('Are you sure you want to delete the uploaded resume PDF?')) {
      return
    }

    try {
      const fileName = resume.pdf_url.split('/').pop()
      if (fileName) {
        await supabase.storage.from('resumes').remove([fileName])
      }
      setResume({ ...resume, pdf_url: undefined })
      await updateResume({ ...resume, pdf_url: undefined })
      alert('Resume PDF deleted successfully!')
    } catch (error) {
      console.error('Error deleting PDF:', error)
      alert('Error deleting PDF. Please try again.')
    }
  }

  const handleExtractFromPdf = async () => {
    if (!resume?.pdf_url) {
      alert('Please upload a PDF first')
      return
    }

    setExtracting(true)
    try {
      // Extract text from PDF
      const text = await extractTextFromPdf(resume.pdf_url)
      
      // Parse the text (now async with AI extraction)
      const parsed = await parseResumeText(text)
      
      // Convert to Resume format
      const preview: Resume = {
        ...resume,
        name: parsed.name || resume.name,
        title: parsed.title || resume.title,
        email: parsed.email || resume.email,
        phone: parsed.phone || resume.phone,
        location: parsed.location || resume.location,
        summary: parsed.summary || resume.summary,
        experience: parsed.experience.map((exp, idx) => ({
          ...exp,
          id: crypto.randomUUID(),
          order: idx
        })),
        education: parsed.education.map((edu, idx) => ({
          ...edu,
          id: crypto.randomUUID(),
          order: idx
        })),
        skills: parsed.skills.length > 0 ? parsed.skills : resume.skills
      }
      
      setExtractionPreview(preview)
      setShowPreview(true)
    } catch (error: any) {
      console.error('Error extracting from PDF:', error)
      const errorMessage = error?.message || 'Unknown error occurred'
      alert(`Error extracting data from PDF: ${errorMessage}\n\nPlease ensure:\n- The PDF file is valid and not corrupted\n- The PDF is accessible and not password-protected\n- Your internet connection is stable`)
    } finally {
      setExtracting(false)
    }
  }

  const handleApplyExtraction = () => {
    if (extractionPreview) {
      setResume(extractionPreview)
      setShowPreview(false)
      setExtractionPreview(null)
      alert('Extracted data applied! Please review and save when ready.')
    }
  }

  const handleCancelExtraction = () => {
    setShowPreview(false)
    setExtractionPreview(null)
  }

  if (loading) {
    return <div className="text-text-primary/70">Loading resume...</div>
  }

  if (!resume) {
    return <div className="text-text-primary/70">No resume found</div>
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8">
        <h2 className="text-2xl sm:text-3xl font-semibold text-text-primary">Resume</h2>
        <motion.button
          onClick={handleSave}
          disabled={saving}
          className="px-5 sm:px-6 py-2.5 sm:py-3 neu rounded-xl font-medium text-text-primary neu-hover touch-manipulation w-full sm:w-auto text-sm sm:text-base disabled:opacity-50"
          whileHover={saving ? {} : { scale: 1.02, y: -1 }}
          whileTap={saving ? {} : { scale: 0.98 }}
          transition={springConfig}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </motion.button>
      </div>

      <div className="space-y-6 sm:space-y-8">
        {/* PDF Upload */}
        <div className="neu rounded-2xl p-4 sm:p-6">
          <h3 className="text-xl font-semibold mb-4 text-text-primary">Resume PDF</h3>
          <div className="space-y-4">
            {resume.pdf_url ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 neu rounded-xl">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">📄</span>
                    <div>
                      <p className="text-sm font-medium text-text-primary">PDF uploaded</p>
                      <a
                        href={resume.pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-accent hover:underline"
                      >
                        View PDF
                      </a>
                    </div>
                  </div>
                  <motion.button
                    onClick={handleDeletePdf}
                    className="px-4 py-2 neu rounded-xl text-sm text-red-400 hover:text-red-500 transition-colors neu-hover touch-manipulation"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    transition={springConfig}
                  >
                    Delete
                  </motion.button>
                </div>
                <motion.button
                  onClick={handleExtractFromPdf}
                  disabled={extracting}
                  className="w-full px-4 py-3 neu rounded-xl text-sm font-medium text-accent hover:text-accent-secondary transition-colors neu-hover touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed"
                  whileHover={extracting ? {} : { scale: 1.02, y: -1 }}
                  whileTap={extracting ? {} : { scale: 0.98 }}
                  transition={springConfig}
                >
                  {extracting ? 'Extracting...' : 'Extract Data from PDF'}
                </motion.button>
              </div>
            ) : (
              <div className="p-4 neu rounded-xl">
                <p className="text-sm text-text-primary/70 mb-4">No PDF uploaded yet</p>
              </div>
            )}
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                Upload Resume PDF
              </label>
              <input
                type="file"
                accept="application/pdf"
                onChange={handlePdfUpload}
                disabled={uploading}
                className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-sm touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed"
              />
              {uploading && (
                <p className="mt-2 text-sm text-accent">Uploading...</p>
              )}
              <p className="mt-2 text-xs text-text-primary/50">Maximum file size: 10MB</p>
            </div>
          </div>
        </div>

        {/* Extraction Preview Modal */}
        {showPreview && extractionPreview && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="neu rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <h3 className="text-2xl font-semibold mb-4 text-text-primary">Extracted Resume Data</h3>
              <p className="text-sm text-text-primary/70 mb-6">Review the extracted data below. Click "Apply" to use it or "Cancel" to keep your current data.</p>
              
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-xs font-medium mb-1 text-text-primary/70 uppercase">Name</label>
                  <p className="text-text-primary">{extractionPreview.name || '(not found)'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1 text-text-primary/70 uppercase">Title</label>
                  <p className="text-text-primary">{extractionPreview.title || '(not found)'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1 text-text-primary/70 uppercase">Email</label>
                  <p className="text-text-primary">{extractionPreview.email || '(not found)'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1 text-text-primary/70 uppercase">Phone</label>
                  <p className="text-text-primary">{extractionPreview.phone || '(not found)'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1 text-text-primary/70 uppercase">Location</label>
                  <p className="text-text-primary">{extractionPreview.location || '(not found)'}</p>
                </div>
                {extractionPreview.summary && (
                  <div>
                    <label className="block text-xs font-medium mb-1 text-text-primary/70 uppercase">Summary</label>
                    <p className="text-text-primary/70 text-sm">{extractionPreview.summary}</p>
                  </div>
                )}
                {extractionPreview.experience.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium mb-2 text-text-primary/70 uppercase">Experience ({extractionPreview.experience.length} found)</label>
                    <div className="space-y-2">
                      {extractionPreview.experience.slice(0, 3).map((exp, idx) => (
                        <div key={idx} className="p-3 neu rounded-xl text-sm">
                          <p className="font-medium text-text-primary">{exp.title} at {exp.company}</p>
                          {exp.years && <p className="text-text-primary/70 text-xs">{exp.years}</p>}
                        </div>
                      ))}
                      {extractionPreview.experience.length > 3 && (
                        <p className="text-xs text-text-primary/50">+ {extractionPreview.experience.length - 3} more</p>
                      )}
                    </div>
                  </div>
                )}
                {extractionPreview.education.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium mb-2 text-text-primary/70 uppercase">Education ({extractionPreview.education.length} found)</label>
                    <div className="space-y-2">
                      {extractionPreview.education.map((edu, idx) => (
                        <div key={idx} className="p-3 neu rounded-xl text-sm">
                          <p className="font-medium text-text-primary">{edu.degree}</p>
                          <p className="text-text-primary/70 text-xs">{edu.university}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {extractionPreview.skills.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium mb-2 text-text-primary/70 uppercase">Skills ({extractionPreview.skills.length} found)</label>
                    <div className="flex flex-wrap gap-2">
                      {extractionPreview.skills.slice(0, 10).map((skill, idx) => (
                        <span key={idx} className="px-2 py-1 neu rounded-lg text-xs text-accent">{skill}</span>
                      ))}
                      {extractionPreview.skills.length > 10 && (
                        <span className="text-xs text-text-primary/50">+ {extractionPreview.skills.length - 10} more</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex gap-3">
                <motion.button
                  onClick={handleApplyExtraction}
                  className="flex-1 px-4 py-3 bg-accent text-white rounded-xl font-medium hover:bg-accent/90 transition-colors neu-hover"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  transition={springConfig}
                >
                  Apply
                </motion.button>
                <motion.button
                  onClick={handleCancelExtraction}
                  className="flex-1 px-4 py-3 neu rounded-xl font-medium text-text-primary neu-hover"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  transition={springConfig}
                >
                  Cancel
                </motion.button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Basic Info */}
        <div className="neu rounded-2xl p-4 sm:p-6">
          <h3 className="text-xl font-semibold mb-4 text-text-primary">Basic Information</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                Name
              </label>
              <input
                type="text"
                value={resume.name || ''}
                onChange={(e) => updateField('name', e.target.value)}
                className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                Title
              </label>
              <input
                type="text"
                value={resume.title || ''}
                onChange={(e) => updateField('title', e.target.value)}
                className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                Email
              </label>
              <input
                type="email"
                value={resume.email || ''}
                onChange={(e) => updateField('email', e.target.value)}
                className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                Phone
              </label>
              <input
                type="text"
                value={resume.phone || ''}
                onChange={(e) => updateField('phone', e.target.value)}
                className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs sm:text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                Location
              </label>
              <input
                type="text"
                value={resume.location || ''}
                onChange={(e) => updateField('location', e.target.value)}
                className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs sm:text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                Summary
              </label>
              <textarea
                value={resume.summary || ''}
                onChange={(e) => updateField('summary', e.target.value)}
                rows={4}
                className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation resize-y"
              />
            </div>
          </div>
        </div>

        {/* Experience */}
        <div className="neu rounded-2xl p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-text-primary">Experience</h3>
            <motion.button
              onClick={addExperience}
              className="px-4 py-2 neu rounded-xl text-sm font-medium text-accent neu-hover touch-manipulation"
              whileHover={{ scale: 1.02, y: -1 }}
              whileTap={{ scale: 0.98 }}
              transition={springConfig}
            >
              + Add Experience
            </motion.button>
          </div>
          <div className="space-y-4">
            {resume.experience.map((exp, index) => (
              <div key={exp.id} className="neu p-4 rounded-xl">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <input
                    type="text"
                    value={exp.title}
                    onChange={(e) => updateExperience(index, 'title', e.target.value)}
                    placeholder="Job Title"
                    className="px-4 py-3 neu rounded-xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-sm touch-manipulation"
                  />
                  <input
                    type="text"
                    value={exp.company}
                    onChange={(e) => updateExperience(index, 'company', e.target.value)}
                    placeholder="Company"
                    className="px-4 py-3 neu rounded-xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-sm touch-manipulation"
                  />
                  <input
                    type="text"
                    value={exp.years}
                    onChange={(e) => updateExperience(index, 'years', e.target.value)}
                    placeholder="Years (e.g., 2022 - Present)"
                    className="px-4 py-3 neu rounded-xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-sm touch-manipulation"
                  />
                </div>
                <textarea
                  value={exp.description}
                  onChange={(e) => updateExperience(index, 'description', e.target.value)}
                  placeholder="Description"
                  rows={3}
                  className="w-full px-4 py-3 neu rounded-xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-sm touch-manipulation resize-y mb-4"
                />
                <div className="flex justify-end">
                  <motion.button
                    onClick={() => removeExperience(index)}
                    className="px-3 py-1.5 neu rounded-xl text-xs text-red-400 hover:text-red-500 transition-colors neu-hover touch-manipulation"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    transition={springConfig}
                  >
                    Remove
                  </motion.button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Education */}
        <div className="neu rounded-2xl p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-text-primary">Education</h3>
            <motion.button
              onClick={addEducation}
              className="px-4 py-2 neu rounded-xl text-sm font-medium text-accent neu-hover touch-manipulation"
              whileHover={{ scale: 1.02, y: -1 }}
              whileTap={{ scale: 0.98 }}
              transition={springConfig}
            >
              + Add Education
            </motion.button>
          </div>
          <div className="space-y-4">
            {resume.education.map((edu, index) => (
              <div key={edu.id} className="neu p-4 rounded-xl">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <input
                    type="text"
                    value={edu.degree}
                    onChange={(e) => updateEducation(index, 'degree', e.target.value)}
                    placeholder="Degree"
                    className="px-4 py-3 neu rounded-xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-sm touch-manipulation"
                  />
                  <input
                    type="text"
                    value={edu.university}
                    onChange={(e) => updateEducation(index, 'university', e.target.value)}
                    placeholder="University"
                    className="px-4 py-3 neu rounded-xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-sm touch-manipulation"
                  />
                  <input
                    type="text"
                    value={edu.years}
                    onChange={(e) => updateEducation(index, 'years', e.target.value)}
                    placeholder="Years (e.g., 2016 - 2020)"
                    className="px-4 py-3 neu rounded-xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-sm touch-manipulation"
                  />
                </div>
                <textarea
                  value={edu.details}
                  onChange={(e) => updateEducation(index, 'details', e.target.value)}
                  placeholder="Additional details"
                  rows={2}
                  className="w-full px-4 py-3 neu rounded-xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-sm touch-manipulation resize-y mb-4"
                />
                <div className="flex justify-end">
                  <motion.button
                    onClick={() => removeEducation(index)}
                    className="px-3 py-1.5 neu rounded-xl text-xs text-red-400 hover:text-red-500 transition-colors neu-hover touch-manipulation"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    transition={springConfig}
                  >
                    Remove
                  </motion.button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Skills */}
        <div className="neu rounded-2xl p-4 sm:p-6">
          <h3 className="text-xl font-semibold mb-4 text-text-primary">Skills</h3>
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={skillInput}
              onChange={(e) => setSkillInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  addSkill()
                }
              }}
              className="flex-1 px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation"
              placeholder="Add a skill"
            />
            <motion.button
              onClick={addSkill}
              className="px-4 py-3.5 sm:py-4 neu rounded-xl text-accent hover:text-accent-secondary transition-colors neu-hover touch-manipulation"
              whileHover={{ scale: 1.02, y: -1 }}
              whileTap={{ scale: 0.98 }}
              transition={springConfig}
            >
              Add
            </motion.button>
          </div>
          <div className="flex flex-wrap gap-2">
            {resume.skills.map((skill, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-2 px-3 py-1.5 neu rounded-full text-sm text-accent"
              >
                {skill}
                <button
                  onClick={() => removeSkill(index)}
                  className="hover:text-red-400 transition-colors touch-manipulation"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

